import { forwardRef, useRef, useEffect, useImperativeHandle } from "react";
import { Tabulator, FormatModule, DownloadModule, PageModule, ExportModule } from "tabulator-tables";
import { Col, Row } from "react-bootstrap";
import "tabulator-tables/dist/css/tabulator.min.css";
import "tabulator-tables/dist/css/tabulator_bootstrap5.min.css";
import * as XLSX from "xlsx";
import { SortModule } from "tabulator-tables";

interface Entity {
  entity_name: string | null;
  fiscal_year: number;
  net_revenue: number;
  net_expense: number;
  surdef: number;
}

interface EntitySurpDefTableProps {
  data: Entity[];
  entityName?: string;
}

(window as any).XLSX = XLSX;

Tabulator.registerModule([FormatModule, DownloadModule, PageModule, ExportModule, SortModule]);

const EntitySurpDefTable = forwardRef<typeof Tabulator | null, EntitySurpDefTableProps>(
  ({ 
    data,
    entityName
  }, ref) => {
    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);

    const formatTextForFileDownload = (chartLevel: string, entityName: string) => {
      const textFormatForDownload = `${entityName}_${chartLevel}_expenses`;
      return `${entityName}_${chartLevel}_revenue`;
    }

    // Update Entity Name
    useEffect(() => {
    }, [entityName]);

    useImperativeHandle(ref, () => tabulatorRef.current);

    useEffect(() => {
      if (!tableRef.current) return;

      // Determine mobile layout
      const mobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
        window.innerWidth < 1025;

      // Clear any existing table
      tableRef.current.innerHTML = "";

      tabulatorRef.current = new Tabulator(tableRef.current, {
        data,
        layout: mobile ? "fitDataFill" : "fitColumns",
        pagination: "local",
        paginationSize: 10,
        paginationSizeSelector: [10, 25, 50, 100],
        theme: "bootstrap5",
        headerSort: true,
        initialSort: [
          {
            column: "fiscal_year",
            dir: "desc",
          }
        ],
        columns: [
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-landmark'></i> Entity";
              return element;
            },
            field: "entity_name",
            titleDownload: "Entity",
            headerSort: true,
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-calendar-alt'></i> Fiscal Year";
              return element;
            },
            field: "fiscal_year",
            titleDownload: "Fiscal Year",
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Revenue";
              return element;
            },
            field: "net_revenue",
            formatter: "money",
            sorter: "number",
            titleDownload: "Net Revenue",
            formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Expense";
              return element;
            },
            field: "net_expense",
            formatter: "money",
            sorter: "number",
            titleDownload: "Net Expense",
            formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
          },
          {
            titleFormatter: () => {
              const element = document.createElement("span");
              element.innerHTML = "<i class='fas fa-dollar-sign'></i> Surplus/(Deficit)";
              return element;
            },
            field: "surdef",
            formatter: "money",
            sorter: "number",
            titleDownload: "Surplus/(Deficit)",
            formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
          },
        ],
      });

      const handleResize = () => {
        tabulatorRef.current?.redraw(true);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [data]);

    const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
      const formattedEntityName = entityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
      if (!tabulatorRef.current) return;
      tabulatorRef.current.download(type, `${formattedEntityName}_surplus_deficit.${type}`);
    };

    return (
      <>
        <Row className="py-4 px-4">
          <Col>
            <div className="mb-2 d-flex gap-2">
              <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
            </div>
            <div
              ref={tableRef}
              id="tabulator_table"
              style={{ width: "100%", margin: "0 auto" }}
            />
          </Col>
        </Row>
      </>
    );
  }
);

export default EntitySurpDefTable;